/**
 * Render UTC timestamp from server.
 */
import React from 'react';
import TimeAgo, {type Formatter} from 'react-timeago';

import {
  addMonthsClampedLocal,
  DateFormat,
  differenceInDaysLocal,
  differenceInMonthsLocal,
  format as formatDate,
  isSameYearLocal,
  isValidDate,
  parseDateLocal,
  parseDateTimeUTC,
} from '../../util/date';
import {Icon} from './Icon';
import {Tooltip} from './Tooltip';

type Value = string | number;

type TimestampProps = {
  // Acceptable format: "2022-10-04T14:24:44"
  // Acceptable format: 1704824493 (seconds since epoch)
  value: Value;

  // "relative" - e.g. "2 hours ago"
  // "detailed" - e.g. "Oct 4th at 2:24pm (2 hours ago)"
  format?: 'relative' | 'detailed';

  // By default, a "relative" timestamp will update automatically.
  // If you don't want this behavior you can set this prop to false.
  live?: boolean;

  // If true, will omit the time when it's midnight (00:00)
  dropTimeWhenDefault?: boolean;
};

// Format a time difference to a micro string (1h, 1d, 1w, etc.)
const formatSmallTime = (then: Date): string | null => {
  const now = new Date();
  const diffMs = now.getTime() - then.getTime();

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const monthDiff = differenceInMonthsLocal(now, then);
  const years = Math.trunc(monthDiff / 12);
  if (years > 0) {
    return `${years}y`;
  }

  // Always show months if 3 or more
  if (monthDiff >= 3) {
    return `${monthDiff}mo`;
  }

  // Get remaining days by moving forward the months and checking what's left
  const afterMonths = addMonthsClampedLocal(then, monthDiff);
  const remainingDays = differenceInDaysLocal(now, afterMonths);

  // Show months if exact multiple
  if (monthDiff > 0 && remainingDays === 0) {
    return `${monthDiff}mo`;
  }

  const weeks = Math.round(days / 7);
  if (weeks >= 2) {
    return `${weeks}w`;
  }

  // Show weeks when exact multiple
  if (days >= 7 && days % 7 === 0) {
    return `${weeks}w`;
  }

  // Otherwise use days or more precise units
  if (days >= 1) {
    return days === 1 ? '1d' : `${days}d`;
  } else if (hours >= 1) {
    return hours === 1 ? '1h' : `${hours}h`;
  } else if (minutes >= 1) {
    return minutes === 1 ? '1m' : `${minutes}m`;
  } else if (seconds >= 1) {
    return seconds === 1 ? '1s' : `${seconds}s`;
  } else {
    // If the time is in the future, return null
    return null;
  }
};

// moment's format() shows this for an invalid date
const INVALID_DATE = 'Invalid date';

const formatValid = (date: Date, dateFormat: DateFormat) =>
  isValidDate(date) ? formatDate(date, dateFormat) : INVALID_DATE;

const formatLong = (date: Date) =>
  formatValid(date, DateFormat.WEEKDAY_MONTH_DAYTH_YEAR_AT_TIME_TIMEZONE);

const formatShort = (date: Date) =>
  formatValid(
    date,
    isSameYearLocal(new Date(), date)
      ? DateFormat.SHORT_MONTH_DAYTH_AT_TIME
      : DateFormat.SHORT_MONTH_DAYTH_YEAR_AT_TIME
  );

// Return short and long formatted versions of the provided timestamp.
export const formatTimestamp = (value: string, overrideFormat?: DateFormat) => {
  const then = parseDateTimeUTC(value);
  return {
    long: formatLong(then),
    short: overrideFormat
      ? formatValid(then, overrideFormat)
      : formatShort(then),
    small: formatSmallTime(then),
  };
};

const valueToDate = (value: Value): Date =>
  typeof value === 'number' ? new Date(value * 1000) : parseDateTimeUTC(value);

const timeAgoDate = (date: Date) =>
  isValidDate(date)
    ? formatDate(date, DateFormat.ISO_LOCAL_SECONDS)
    : INVALID_DATE;

// See: https://www.npmjs.com/package/react-timeago#formatter-optional
const TIMEAGO_FORMATTER: Formatter = (
  value,
  unit,
  suffix,
  epochSeconds,
  nextFormatter,
  now
) =>
  unit === 'second'
    ? 'just now'
    : nextFormatter!(value, unit, suffix, epochSeconds, nextFormatter, now);

export const Timestamp = ({
  value,
  format,
  live = true,
  dropTimeWhenDefault = false,
}: TimestampProps) => {
  if (value == null) {
    return <span>{INVALID_DATE}</span>;
  }
  const then = valueToDate(value);
  const long = formatLong(then);
  if (format === 'relative') {
    const timeago = (
      <TimeAgo
        title="" // Suppress the default tooltip
        minPeriod={10}
        formatter={TIMEAGO_FORMATTER}
        date={timeAgoDate(then)}
        live={live}
      />
    );
    return <Tooltip content={long} trigger={timeago} />;
  }

  if (format === 'detailed') {
    const timeago = (
      <TimeAgo
        title="" // Suppress the default tooltip
        minPeriod={10}
        formatter={TIMEAGO_FORMATTER}
        date={timeAgoDate(then)}
        live={live}
      />
    );

    const trigger = (
      <span className="cursor-help">
        {formatShort(then)} ({timeago})
      </span>
    );
    return <Tooltip content={long} trigger={trigger} />;
  }

  // Check if the time is midnight (00:00)
  const isMidnight =
    dropTimeWhenDefault && then.getHours() === 0 && then.getMinutes() === 0;

  // Use different formats based on whether it's midnight
  const short = formatValid(
    then,
    isMidnight
      ? DateFormat.SHORT_MONTH_DAYTH_YEAR_NO_COMMA
      : DateFormat.SHORT_MONTH_DAYTH_YEAR_AT_TIME
  );

  const text = <span>{short}</span>;
  return <Tooltip content={long} trigger={text} />;
};

export const TimestampSmall = ({
  value,
  label,
}: TimestampProps & {label?: string}) => {
  // TODO(react-compiler): reads the current time during render; compiled output
  // would cache it.
  'use no memo';
  /* TimestampSmall displays a small timestamp format, e.g. "1d" or "1w".
     in a nice gray tooltip
   */
  if (value == null) {
    return <span>{INVALID_DATE}</span>;
  }
  const then =
    typeof value === 'number' ? new Date(value) : parseDateLocal(value);
  const small = formatSmallTime(then);
  if (!small) {
    // default to regular timestamp, which expects utc
    return <Timestamp value={value} dropTimeWhenDefault />;
  }
  const text = (
    <div className="flex items-center">
      <Icon name="date" className="mr-3" />
      <span className="font-semibold">
        {label} {small}
      </span>
    </div>
  );
  return <Tooltip content={formatLong(then)} trigger={text} />;
};

export const TimestampRange = ({value, field}: {value: any; field: string}) => {
  const {before, after} = value as {
    before: string;
    after: string;
  };
  return (
    <span className="flex items-center gap-2 font-semibold">
      {field}
      <span className="flex items-center">
        <Timestamp value={after} dropTimeWhenDefault />
      </span>
      <span className="mx-2">→</span>
      <span className="flex items-center">
        <Timestamp value={before} dropTimeWhenDefault />
      </span>
    </span>
  );
};
